// What the next guest pays when a table splits the bill evenly.
//
// The dialog used to divide what was *left* by the number of people at the table, every
// time. Three guests on £32: the first paid £10.67, and the second was then asked for
// £21.33 ÷ 3 = £7.11, a third of the remainder rather than half of it. The bill never
// settled, and the number on screen was wrong with total confidence.
//
// So it divides the remainder by the guests *still to pay*, to the penny, and the last
// guest pays exactly what is left. Rounding each share and letting the last absorb the
// odd penny is what makes the shares add up to the bill: £32 three ways is
// £10.67 + £10.67 + £10.66.

const toPence = (pounds) => Math.round(Number(pounds || 0) * 100);

/** The next guest's share of `remaining`, with `guestsLeft` (including them) still to pay. */
export const splitShare = (remaining, guestsLeft) => {
  const left = Math.max(1, Math.floor(guestsLeft) || 1);
  const pence = toPence(remaining);
  if (left === 1) return pence / 100;
  return Math.round(pence / left) / 100;
};

/** Guests still to pay, the one being served included. Never fewer than one. */
export const guestsLeft = (splitPeople, guestsPaid) => Math.max(1, (splitPeople || 1) - (guestsPaid || 0));

// Where an even split has got to, kept with the order it belongs to.
//
// "Who has paid" used to live in the payment dialog and was thrown away whenever it closed.
// It closes a lot: the till locks after ninety seconds, and a guest finding a card takes
// longer than that. Open it again and the split started over on what was left with the
// head count it began with: £32 three ways, one guest paid, and the next was asked for
// £21.33 ÷ 3 = £7.11, the sum this file was written to stop. So the split is remembered on
// this till against the order, and picked up from where it stopped.

const PREFIX = 'verlo_split_';
const MAX_AGE_MS = 12 * 60 * 60 * 1000; // as an order draft: yesterday's split is nobody's

/** Remember that `paid` of `people` have paid on this order. Before anyone has there is
 * nothing to write (and nothing is forgotten: the dialog starts at nought every time the
 * page loads, which must not wipe a split it is about to pick up). Once everybody has, the
 * split is over. */
export const saveSplit = (orderId, { people, paid }, storage = globalThis.localStorage, now = Date.now()) => {
  if (!orderId || !(paid > 0)) return;
  try {
    if (paid >= people) { storage?.removeItem(`${PREFIX}${orderId}`); return; }
    storage?.setItem(`${PREFIX}${orderId}`, JSON.stringify({ people, paid, at: now }));
  } catch { /* private mode / quota: the split starts over, as it used to */ }
};

export const clearSplit = (orderId, storage = globalThis.localStorage) => {
  if (!orderId) return;
  try { storage?.removeItem(`${PREFIX}${orderId}`); } catch { /* nothing to forget */ }
};

/** The split to carry on with when the dialog opens: `{ people, paid }`, or null to start
 * afresh. Only while it still fits the order: there is money left to pay, and at least as
 * many payments on the order as guests it says have paid (a payment voided since, or a
 * note left by another order, is not a split in progress). */
export const resumeSplit = (orderId, { payments = 0, remaining = 0 } = {}, storage = globalThis.localStorage, now = Date.now()) => {
  if (!orderId) return null;
  try {
    const kept = JSON.parse(storage?.getItem(`${PREFIX}${orderId}`) || 'null');
    if (!kept) return null;
    const people = Math.floor(Number(kept.people));
    const paid = Math.floor(Number(kept.paid));
    const live = now - kept.at <= MAX_AGE_MS && remaining > 0 && paid >= 1 && paid < people && paid <= payments;
    if (!live) { storage?.removeItem(`${PREFIX}${orderId}`); return null; }
    return { people, paid };
  } catch { return null; }
};
